import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { apiCall, ENDPOINTS } from '../../../config/api';
import { FiSave, FiX } from 'react-icons/fi';
import { EmailTemplate } from '../../../types/email';
import DocumentEditor from '../../../components/DocumentEditor';

// Email template variables similar to document template fields
const EMAIL_TEMPLATE_VARIABLES = [
	// Date & Time
	{ label: 'Current Date (Short)', variable: '[CurrentDateShort]', category: 'Date & Time', description: 'DD/MM/YYYY' },
	{ label: 'Current Date (Long)', variable: '[CurrentDateLong]', category: 'Date & Time', description: 'DD Month YYYY' },
	{ label: 'Current Time', variable: '[CurrentTime]', category: 'Date & Time', description: 'HH:MM AM/PM' },
	
	// From/Sender
	{ label: 'Sender Name', variable: '[SenderName]', category: 'Sender', description: 'Full name of sender' },
	{ label: 'Sender Email', variable: '[SenderEmail]', category: 'Sender' },
	{ label: 'Sender Phone', variable: '[SenderPhone]', category: 'Sender' },
	{ label: 'Sender Title', variable: '[SenderTitle]', category: 'Sender', description: 'Dr., Mr., etc.' },
	
	// Recipient
	{ label: 'Recipient Name', variable: '[RecipientName]', category: 'Recipient' },
	{ label: 'Recipient First Name', variable: '[RecipientFirstName]', category: 'Recipient' },
	{ label: 'Recipient Last Name', variable: '[RecipientLastName]', category: 'Recipient' },
	{ label: 'Recipient Email', variable: '[RecipientEmail]', category: 'Recipient' },
	
	// Patient
	{ label: 'Patient Full Name', variable: '[PatientFullName]', category: 'Patient' },
	{ label: 'Patient First Name', variable: '[PatientFirstName]', category: 'Patient' },
	{ label: 'Patient Last Name', variable: '[PatientLastName]', category: 'Patient' },
	{ label: 'Patient DOB', variable: '[PatientDOB]', category: 'Patient' },
	{ label: 'Patient Age', variable: '[PatientAge]', category: 'Patient' },
	{ label: 'Patient MRN', variable: '[PatientMRN]', category: 'Patient', description: 'Medical Record Number' },
	{ label: 'Patient Phone', variable: '[PatientPhone]', category: 'Patient' },
	{ label: 'Patient Email', variable: '[PatientEmail]', category: 'Patient' },
	
	// Appointment
	{ label: 'Appointment Date', variable: '[AppointmentDate]', category: 'Appointment' },
	{ label: 'Appointment Time', variable: '[AppointmentTime]', category: 'Appointment' },
	{ label: 'Appointment Type', variable: '[AppointmentType]', category: 'Appointment' },
	{ label: 'Appointment Provider', variable: '[AppointmentProvider]', category: 'Appointment' },
	
	// Clinic
	{ label: 'Clinic Name', variable: '[ClinicName]', category: 'Clinic' },
	{ label: 'Clinic Address', variable: '[ClinicAddress]', category: 'Clinic' },
	{ label: 'Clinic Phone', variable: '[ClinicPhone]', category: 'Clinic' },
	{ label: 'Clinic Email', variable: '[ClinicEmail]', category: 'Clinic' },
];

const EmailTemplateEditor: React.FC = () => {
	const { token } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const editorRef = useRef<any>(null);
	
	const editingTemplate = location.state?.template as EmailTemplate | undefined;
	
	const [templateName, setTemplateName] = useState('');
	const [subjectTemplate, setSubjectTemplate] = useState('');
	const [category, setCategory] = useState<string>('general');
	const [description, setDescription] = useState('');
	const [bodyTemplate, setBodyTemplate] = useState('');
	const [isActive, setIsActive] = useState(true);
	const [saving, setSaving] = useState(false);
	const [selectedCategory, setSelectedCategory] = useState<string>('All');
	const [searchField, setSearchField] = useState('');
	
	useEffect(() => {
		// If editing, populate fields
		if (editingTemplate) {
			setTemplateName(editingTemplate.name);
			setSubjectTemplate(editingTemplate.subject_template);
			setCategory(editingTemplate.category || 'general');
			setDescription(editingTemplate.description || '');
			setBodyTemplate(editingTemplate.body_template);
			setIsActive(editingTemplate.is_active);
		}
	}, []);
	
	const categories = ['All', ...Array.from(new Set(EMAIL_TEMPLATE_VARIABLES.map(f => f.category)))];
	
	const filteredFields = EMAIL_TEMPLATE_VARIABLES.filter(field => {
		const matchesCategory = selectedCategory === 'All' || field.category === selectedCategory;
		const matchesSearch = !searchField || 
			field.label.toLowerCase().includes(searchField.toLowerCase()) ||
			field.variable.toLowerCase().includes(searchField.toLowerCase());
		return matchesCategory && matchesSearch;
	});
	
	const handleFieldDragStart = (e: React.DragEvent, field: typeof EMAIL_TEMPLATE_VARIABLES[0]) => {
		e.dataTransfer.setData('text/plain', field.variable);
		e.dataTransfer.effectAllowed = 'copy';
	};
	
	const handleSave = async () => {
		if (!templateName || !subjectTemplate) {
			alert('Please enter a template name and subject template');
			return;
		}
		
		if (!token) return;
		
		setSaving(true);
		
		try {
			const templateData = {
				name: templateName,
				subject_template: subjectTemplate,
				body_template: bodyTemplate,
				category,
				description,
				is_active: isActive,
			};
			
			const url = editingTemplate 
				? `${ENDPOINTS.EMAIL.TEMPLATES}${editingTemplate.id}/`
				: ENDPOINTS.EMAIL.TEMPLATES;
			
			const method = editingTemplate ? 'PUT' : 'POST';
			
			const response = await apiCall(url, {
				method,
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(templateData),
			}, token);
			
			if (response.ok) {
				navigate('/email/templates');
			} else {
				alert('Failed to save template');
			}
		} catch (error) {
			console.error('Error saving template:', error);
			alert('Failed to save template');
		} finally {
			setSaving(false);
		}
	};
	
	const handleCancel = () => {
		if (window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
			navigate('/email/templates');
		}
	};
	
	return (
		<div className="fixed inset-0 z-50 bg-base-100 flex flex-col">
			{/* Header */}
			<div className="bg-base-200 border-b border-base-300 px-6 py-4 flex items-center justify-between shrink-0">
				<div className="flex items-center gap-4 flex-1">
					<div className="flex-1 max-w-md">
						<input
							type="text"
							value={templateName}
							onChange={(e) => setTemplateName(e.target.value)}
							placeholder="Template Name"
							className="input input-bordered w-full"
						/>
					</div>
					<select
						value={category}
						onChange={(e) => setCategory(e.target.value)}
						className="select select-bordered w-48"
					>
						<option value="general">General</option>
						<option value="appointment">Appointment</option>
						<option value="follow-up">Follow-up</option>
						<option value="referral">Referral</option>
					</select>
					<label className="flex items-center gap-2 cursor-pointer">
						<input
							type="checkbox"
							checked={isActive}
							onChange={(e) => setIsActive(e.target.checked)}
							className="checkbox checkbox-primary checkbox-sm"
						/>
						<span className="text-sm">Active</span>
					</label>
				</div>
				<div className="flex items-center gap-2">
					<button
						onClick={handleSave}
						disabled={saving}
						className="btn btn-primary gap-2"
					>
						<FiSave className="w-4 h-4" />
						{saving ? 'Saving...' : 'Save Template'}
					</button>
					<button
						onClick={handleCancel}
						className="btn btn-ghost gap-2"
					>
						<FiX className="w-4 h-4" />
						Cancel
					</button>
				</div>
			</div>
			
			{/* Main Content Area */}
			<div className="flex-1 flex overflow-hidden min-h-0">
				{/* Editor Area */}
				<div className="flex-1 flex flex-col overflow-hidden min-w-0">
					{/* Description & Subject Template */}
					<div className="px-6 py-3 border-b border-base-300 shrink-0 space-y-2">
						<input
							type="text"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							placeholder="Template Description (optional)"
							className="input input-bordered input-sm w-full"
						/>
						<input
							type="text"
							value={subjectTemplate}
							onChange={(e) => setSubjectTemplate(e.target.value)}
							placeholder="Subject Template (e.g., Appointment Confirmation for [PatientFullName])"
							className="input input-bordered w-full"
						/>
						<p className="text-xs opacity-60">
							💡 Use variables like [PatientFullName], [AppointmentDate], etc. in the subject
						</p>
					</div>
					
					{/* Document Editor */}
					<div className="flex-1 min-h-0">
						<DocumentEditor
							ref={editorRef}
							initialContent={bodyTemplate}
							onChange={setBodyTemplate}
							isReadOnly={false}
							height="100%"
						/>
					</div>
				</div>
				
				{/* Variables Sidebar */}
				<div className="w-80 border-l border-base-300 flex flex-col bg-base-200">
					<div className="p-4 border-b border-base-300">
						<h3 className="font-semibold mb-3">Template Variables</h3>
						
						{/* Search */}
						<input
							type="text"
							value={searchField}
							onChange={(e) => setSearchField(e.target.value)}
							placeholder="Search variables..."
							className="input input-bordered input-sm w-full mb-3"
						/>
						
						{/* Category Tabs */}
						<div className="flex flex-wrap gap-1">
							{categories.map(cat => (
								<button
									key={cat}
									onClick={() => setSelectedCategory(cat)}
									className={`btn btn-xs ${
										selectedCategory === cat
											? 'btn-primary'
											: 'btn-ghost'
									}`}
								>
									{cat}
								</button>
							))}
						</div>
					</div>
					
					{/* Variables List */}
					<div className="flex-1 overflow-y-auto p-4 space-y-2">
						{filteredFields.map(field => (
							<div
								key={field.variable}
								draggable
								onDragStart={(e) => handleFieldDragStart(e, field)}
								className="bg-base-100 p-3 rounded-lg border border-base-300 cursor-move hover:border-primary hover:shadow-md transition-all"
								title={field.description}
							>
								<div className="font-medium text-sm">{field.label}</div>
								<div className="text-xs opacity-60 font-mono mt-1">{field.variable}</div>
								{field.description && (
									<div className="text-xs opacity-50 mt-1">{field.description}</div>
								)}
							</div>
						))}
						
						{filteredFields.length === 0 && (
							<div className="text-center text-sm opacity-50 py-8">
								No variables found
							</div>
						)}
					</div>
					
					{/* Instructions */}
					<div className="p-4 border-t border-base-300 bg-base-100">
						<div className="text-xs opacity-70">
							<p className="font-semibold mb-1">💡 How to use:</p>
							<p className="mb-2">Drag and drop variables into the editor to add them to your template.</p>
							<p className="font-semibold mb-1">Examples:</p>
							<p className="mb-1">• Subject: "Appointment for [PatientFullName]"</p>
							<p>• Body: "Dear [RecipientFirstName], Your appointment with [ClinicName] is scheduled for [AppointmentDate]..."</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default EmailTemplateEditor;
